import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  homePathFor,
  useHomePersona,
  type HomePersona,
} from "@shared-core/domain/homePersona";
import {
  isScopeOverrideRole,
  useScopeOverrideValue,
} from "@shared-core/domain/scopeOverride";
import type { CsrHomeDashboard } from "@shared-core/data/api-types";
import { useAuth } from "../app/auth/hooks";
import { HomePersonaToggle } from "../app/components/patterns/HomePersonaToggle";
import {
  CommandCentreStatStrip,
  type CommandCentreStatTileKey,
} from "../app/components/domain/csr-command-centre/CommandCentreStatStrip";
import { CommandCentreReadinessStrip } from "../app/components/domain/csr-command-centre/CommandCentreReadinessStrip";
import { CommandCentreRail } from "../app/components/domain/csr-command-centre/CommandCentreRail";
import {
  PortfolioBoard,
  type PortfolioBoardGroup,
  type PortfolioBoardHandle,
} from "../app/components/domain/csr-command-centre/PortfolioBoard";
import { SalesOrdersNeedingAttentionSection } from "../app/components/domain/csr-command-centre/SalesOrdersNeedingAttentionSection";
import { NeedsAttentionPanel } from "../app/components/domain/needs-attention/NeedsAttentionPanel";
import { isFeatureEnabled } from "@shared-core/config/features";
import type { WorkUnitSummaryItem } from "@shared-core/data/api-types";

/**
 * CSR home — the "Portfolio board" command centre (approved Variant B —
 * see docs/features/csr-command-centre/). The CSR's jobs grouped by state,
 * each carrying real email/meeting comms counts, a
 * `SalesOrdersNeedingAttentionSection` below the board for Tharstern sales
 * orders that are Confirmed but not (fully) allocated (see
 * docs/features/sales-orders-needing-attention/), and a right rail (tickets,
 * upcoming meetings, alerts).
 * Backed by `DataAdapter.getCsrHome()` (`GET /views/csr-home`), which now
 * returns the enriched `CsrHomeDashboard` shape.
 *
 * `board` groups the dashboard's already worst-first-sorted arrays.
 * `not_production_ready`, `due_today` and `due_soon` are their own lanes,
 * passed straight through as the backend's own arrays (`due_today`/
 * `due_soon` split out per direct CSR feedback: "jobs due out that day"
 * needed its own view, not merged with "due soon"). Only `at_risk_late`
 * still concatenates two arrays (`overdue`+`at_risk`) — an overdue job's
 * `required_at` always precedes an at-risk job's, so the combined list
 * stays worst-first with no re-sort needed. This groups data already
 * fetched in one composed response; it is not a cross-resource join.
 *
 * `PortfolioBoard` renders "Not production ready" first — it's upstream of
 * every deadline lane below it (see PortfolioBoard.tsx) — then "Due today"
 * and "Due soon" ahead of "At risk / Late" — per direct user feedback
 * (2026-09-10) that the due-date lanes are the more actionable list (a job
 * can show up in both, and seeing it under "due today" first avoids the
 * double-take of also spotting it under "at risk"). This page's subtitle
 * below mirrors that order.
 *
 * The job stat tiles (`CommandCentreStatStrip`) are clickable: clicking one
 * scrolls its board lane into view and expands it past any
 * preview/collapsed state, via `PortfolioBoard`'s imperative handle.
 */
export function CsrHome() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPersona } = useHomePersona({ role: undefined });
  // Page-local mirror of the account-menu's scope-override toggle — see
  // `useScopeOverrideValue`'s docstring. AccountMenu owns the header-provider
  // registration; this instance only reads/writes storage and stays in sync
  // via the shared listener set both hooks subscribe to.
  const { showAll, setShowAll } = useScopeOverrideValue({ role: user?.role });
  const canToggleScope = isScopeOverrideRole(user?.role);
  const [dashboard, setDashboard] = useState<CsrHomeDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const boardRef = useRef<PortfolioBoardHandle>(null);

  const switchPersona = (next: HomePersona) => {
    setPersona(next);
    navigate(homePathFor(next));
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await dataAdapter.getCsrHome();
        if (!cancelled) setDashboard(result);
      } catch (err) {
        console.error("Error loading CSR dashboard:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading your dashboard…</div>
      </div>
    );
  }

  const board: Record<PortfolioBoardGroup, WorkUnitSummaryItem[]> = {
    not_production_ready: dashboard.not_production_ready,
    at_risk_late: [...dashboard.overdue, ...dashboard.at_risk],
    due_today: dashboard.due_today,
    due_soon: dashboard.due_soon,
    wip: dashboard.wip,
    recently_completed: dashboard.recent_completions,
    finished_awaiting_close: dashboard.finished_awaiting_close,
  };

  function focusBoardGroup(group: PortfolioBoardGroup) {
    boardRef.current?.focusGroup(group);
  }

  // Stat-tile key -> board lane. "Overdue" maps to the merged
  // `at_risk_late` lane (see `PortfolioBoard`); every other tile key matches
  // its lane name directly.
  const TILE_TO_BOARD_GROUP: Record<
    CommandCentreStatTileKey,
    PortfolioBoardGroup
  > = {
    overdue: "at_risk_late",
    due_today: "due_today",
    due_soon: "due_soon",
    not_production_ready: "not_production_ready",
  };

  function selectStatTile(tile: CommandCentreStatTileKey) {
    focusBoardGroup(TILE_TO_BOARD_GROUP[tile]);
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-1 gap-4 flex-wrap">
          <h1 className="text-2xl">CSR/AM command centre</h1>
          <div className="flex items-center gap-3">
            {canToggleScope && (
              <button
                type="button"
                aria-pressed={showAll}
                onClick={() => setShowAll(!showAll)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  showAll
                    ? "bg-primary text-white border-primary"
                    : "bg-surface border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                Show all customers
              </button>
            )}
            <HomePersonaToggle current="csr" onSelect={switchPersona} />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Your jobs, worst first — what hasn&apos;t started moving, what&apos;s
          due, and what&apos;s late.
        </p>
      </div>

      {/* Portfolios are derived from Tharstern's CSR/rep columns, not
          assigned directly — a customer that never got mapped to this user
          means an empty portfolio, which used to look identical to "no work
          right now" (the incident that motivated this banner).
          `=== 0` is deliberate: `assigned_customer_count` is also `null` for
          an unscoped caller (admin, or scope-override on), and a bare
          falsiness check would wrongly show this banner for them too. */}
      {dashboard.assigned_customer_count === 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <AlertTriangle className="size-4 mt-0.5 shrink-0" />
          <p>
            <span className="font-medium">
              No accounts are allocated to you in Tharstern yet.
            </span>{" "}
            This board only shows jobs you&apos;ve booked in yourself —
            customer-scoped rails like upcoming meetings will stay empty until
            you have a portfolio. If you look after customers that aren&apos;t
            showing, claim them from their customer page
            {canToggleScope
              ? ', or use "Show all customers" above to see everything in the meantime.'
              : "."}
          </p>
        </div>
      )}

      <CommandCentreStatStrip
        counts={dashboard.summary_counts}
        onSelectTile={selectStatTile}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* CIS's own alerts (v1 completion I §10), while v1 completion is on. */}
          {isFeatureEnabled("v1_completion") && isFeatureEnabled("orders") && (
            <NeedsAttentionPanel />
          )}
          <CommandCentreReadinessStrip entries={dashboard.readiness_pipeline} />
          <PortfolioBoard
            ref={boardRef}
            board={board}
            notProductionReadyCount={
              dashboard.summary_counts.not_production_ready_count
            }
          />
          <SalesOrdersNeedingAttentionSection
            orders={dashboard.sales_orders_needing_attention}
            totalCount={
              dashboard.summary_counts.sales_orders_needing_attention_count
            }
          />
        </div>
        <div>
          <CommandCentreRail
            upcomingMeetings={dashboard.upcoming_meetings}
            tickets={dashboard.tickets}
            alerts={dashboard.alerts}
          />
        </div>
      </div>
    </div>
  );
}
